import type { IAppResources } from './resources/_IAppResources';

import { CookieProvider } from '@powerduck/common/utils/cookie';
import { ModalSectionMode } from '@powerduck/common/enums/modal';
import { Temporal } from '@js-temporal/polyfill';


import { LanguageProvider } from './resources/_LanguageProvider';
import { AppConfig } from './app-config';
import { AppRouter } from './app/router';
import { IVue, IVueRouter, IAppRoute } from './app/router-interface';
import { Language } from '@powerduck/common/enums/language';
import StorageProvider from './.powerduck/common/local-storage-shim';

export class AppState {
	static _stickyMap: { [index: string]: IStickyDeclaration } = {};
	static _vueInstance: IVue = null;
	static _state: IInitialState = null;
	private static _modalSectionMode: ModalSectionMode | null = null;
	private static _storagePrefix = 'myAppName';

	static get appName(): string {
		return 'Inviton';
	}

	static get router(): IVueRouter {
		return AppRouter.currentRouter;
	}

	static get currentAppRoute(): IAppRoute {
		// Vue Router 4: currentRoute is now a ref, access via .value
		let retVal = AppRouter.getRouteByName(AppState.router.currentRoute.value.name as string);
		if (retVal == null) {
			retVal = AppRouter.getPossibleInitialRoute();
		}

		return retVal;
	}

	static get currentLanguage(): Language {
		return AppState.initialState.Language;
	}

	static get currentLanguageCode(): string {
		return AppState.currentLanguage as string;
	}

	static get resources(): IAppResources {
		return LanguageProvider.getResourcePack(AppState.currentLanguage);
	}

	static get user(): ISessionUser {
		return AppState.initialState.User;
	}

	static get instanceConfig(): IInstanceConfig {
		return AppState.initialState.InstanceConfig;
	}

	static registerStickyTabs(id: string, state: IStickyDeclaration): void {
		AppState._stickyMap[id] = state;
	}

	static unregisterStickyTabs(id: string): void {
		if (AppState._stickyMap[id] != null) {
			try {
				delete AppState._stickyMap[id];
			} catch (e) { }
		}
	}

	static setLanguage(langCode: string, redir?: boolean): void {
		const expirationDate = Temporal.Now.plainDateTimeISO().add({ years: 10 });
		CookieProvider.set(
			'invLang',
			langCode,
			expirationDate,
		);

		if (redir == true) {
			let docUrl = document.URL;
			docUrl = docUrl
				.replace(new RegExp('[?&]' + 'lang' + '=[^&#]*(#.*)?$'), '$1')
				.replace(new RegExp('([?&])' + 'lang' + '=[^&]*&'), '$1')
				.replace(new RegExp('[?&]' + 'language' + '=[^&#]*(#.*)?$'), '$1')
				.replace(new RegExp('([?&])' + 'language' + '=[^&]*&'), '$1');

			if (docUrl.indexOf('/vstupenky-online') > -1) {
				docUrl = '/event-list';
			}

			$('body').html(AppConfig.intransparentBlockerHtml);
			setTimeout(() => {
				location.href = docUrl;
			}, 150);
		}
	}

	static setUser(userData: ISessionUser): void {
		AppState.initialState.User = userData;
		// Vue 3: Reactivity is handled differently - no $children access
		// Components should use computed properties or watchers on AppState.user
	}

	static get modalSectionMode(): ModalSectionMode {
		if (AppState._modalSectionMode == null) {
			const stored = localStorage.getItem(AppState._storagePrefix + 'modalSectionMode');
			if (stored != null) {
				AppState._modalSectionMode = Number(stored) as ModalSectionMode;
			} else {
				AppState._modalSectionMode = ModalSectionMode.navPills;
			}
		}
		return AppState._modalSectionMode;
	}

	static setModalSectionMode(mode: ModalSectionMode): void {
		localStorage.setItem(AppState._storagePrefix + 'modalSectionMode', mode.toString());
		AppState._modalSectionMode = mode;
	}

	static get stickyTabs(): IStickyDeclaration {
		for (const id in AppState._stickyMap) {
			if (AppState._stickyMap[id].HasSticky) {
				return AppState._stickyMap[id];
			}
		}

		return {
			AllowTabChange: true,
			HasSticky: false,
		};
	}

	private static get initialState(): IInitialState {
		if (this._state != null) {
			return this._state;
		}

		const serverProvided = (window as any).initialState;
		if (serverProvided != null) {
			this._state = serverProvided;
			return this._state;
		}
	}
}

export interface IStickyDeclaration {
	HasSticky: boolean;
	AllowTabChange: boolean;
}

export interface IInitialState {
	Language: Language;
	User: ISessionUser;
	InstanceConfig: IInstanceConfig;
}

export interface ISessionUser {
	Email: string;
	DisplayName: string;
}

export interface IInstanceConfig {
	Company: ICompanyData;
	PortalSettings: IPortalSettings;
}

export interface ICompanyData {
	BusinessName: string;
	Street: string;
	ZIP: string;
	City: string;
	CountryISO: string;
	CompanyID: string;
	TaxID: string;
	VatID: string;
}

export interface IPortalSettings {
	LogoUrlWhiteSquare: string;
	LogoUrlWhiteTextOnly: string;
	LogoUrlWhite: string;
	LogoUrlWhitePng: string;
	LogoUrlBlack: string;
	LoginBackgroundUrl: string;
	FavIcon: string;

	FacebookUrl: string;
	InstagramUrl: string;
	GooglePlusUrl: string;

	CustomCssPath: string;
	EmailBackColor: string;
}

window['AppState'] = AppState;
